import { useEffect, useState, useCallback } from "react";
import Navbar from "../components/Navbar";
import '../styles/sessions.css';
import API from "../services/api";

const STATUS_OPTIONS = [
    "",
    "Session Started",
    "Home",
    "Product Listing",
    "Product Detail",
    "Add to Cart",
    "Checkout",
    "Shipping Saved",
    "Shipping Selected",
    "Gift Card Applied",
    "Checkout Email Sent",
    "Payment Selected",
    "Payment Processing",
    "Payment Complete",
    "Order Successful",
    "Order Error",
    "Abandoned",
    "Sleep",
];
const LIMIT_OPTIONS  = [10, 20, 50, 100];

const CELL  = { borderBottom: "1px solid #ccc", padding: "10px" };
const INPUT = {
    padding: "6px 10px", borderRadius: "4px",
    border: "1px solid #ccc", fontSize: "13px",
};
const BTN = (disabled) => ({
    padding: "6px 14px", borderRadius: "4px", cursor: disabled ? "not-allowed" : "pointer",
    border: "1px solid #ccc", background: disabled ? "#f0f0f0" : "#fff",
    color: disabled ? "#aaa" : "#333", fontSize: "13px",
});

const toIsoIfPresent = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString();
};

const toEpochMsIfPresent = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "" : String(parsed.getTime());
};

const getStartOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
};

const getNow = () => {
    const d = new Date();
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
};

// ✅ Single source of truth for default filter values
const defaultFilters = () => ({
    status:     "",
    search:     "",
    ip:         "",
    city:       "",
    startDate:  getStartOfToday(),
    endDate:    getNow(),
    store_code: "",
});

const COLUMNS = [
    { label: "Session",         field: null           },
    { label: "Store",           field: null           },
    { label: "Started At",      field: "first_event"  },
    { label: "Last Updated At", field: "last_event"   },
    { label: "IP",              field: "ip_address"   },
    { label: "City",            field: null           },
    { label: "Region",          field: null           },
    { label: "Event Count",     field: "total_events" },
    { label: "Status",          field: "status"       },
];

function SortIcon({ active, order }) {
    return (
        <span style={{ marginLeft: "5px", opacity: active ? 1 : 0.3, fontSize: "11px" }}>
            {active ? (order === "asc" ? "▲" : "▼") : "▲▼"}
        </span>
    );
}

function Sessions() {
    // ✅ Both filters and applied start with the SAME default values
    const [filters, setFilters] = useState(defaultFilters);
    const [applied, setApplied] = useState(defaultFilters);

    const [sortBy,    setSortBy]    = useState("first_event");
    const [sortOrder, setSortOrder] = useState("desc");

    const [page,       setPage]       = useState(1);
    const [limit,      setLimit]      = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [total,      setTotal]      = useState(0);

    const [sessions,  setSessions]  = useState([]);
    const [loading,   setLoading]   = useState(false);
    const [error,     setError]     = useState(null);
    const [botFilter, setBotFilter] = useState('exclude');
    const [healthFilter, setHealthFilter] = useState('');  // green | orange | red | empty

    const fetchSessions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page,
                limit,
                sortBy,
                sortOrder,
                ...applied,
                startDate:   toIsoIfPresent(applied.startDate),
                endDate:     toIsoIfPresent(applied.endDate),
                startDateMs: toEpochMsIfPresent(applied.startDate),
                endDateMs:   toEpochMsIfPresent(applied.endDate),
            };

            if (botFilter)    params.botFilter    = botFilter;
            if (healthFilter) params.healthFilter = healthFilter;

            // Remove empty string values so they don't reach the backend
            Object.keys(params).forEach(k => params[k] === "" && delete params[k]);

            const res = await API.get("/sessions", {
                params,
                headers: { Authorization: localStorage.getItem("token") },
            });

            setSessions(res.data.data);
            setTotalPages(res.data.pagination.totalPages);
            setTotal(res.data.pagination.total);
        } catch (err) {
            setError("Failed to load sessions.");
        } finally {
            setLoading(false);
        }
    }, [page, limit, sortBy, sortOrder, applied, botFilter, healthFilter]);

    useEffect(() => { fetchSessions(); }, [fetchSessions]);

    const handleFilterChange = (e) =>
        setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const applyFilters = () => {
        setPage(1);
        setApplied({ ...filters });
    };

    const clearFilters = () => {
        const empty = defaultFilters();
        setFilters(empty);
        setApplied(empty);
        setPage(1);
    };

    const handleSort = (field) => {
        if (!field) return;
        if (sortBy === field) {
            setSortOrder(o => o === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortOrder("desc");
        }
        setPage(1);
    };

    return (
        <div className="dashboard-container">
            <Navbar/>
            <div style={{fontFamily: "sans-serif"}}>
                <div className="filters">
                    <label style={{display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px", color: "#555"}}>
                        Show Bot Logs
                        <select value={botFilter} onChange={(e) => setBotFilter(e.target.value)}>
                            <option value="exclude">Hide Bots</option>
                            <option value="include">Show All</option>
                            <option value="only">Only Bots</option>
                        </select>
                    </label>
                    {/* ✅ Session health filter */}
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        {[
                            { value: "",       label: "All",    bg: "#f3f4f6", color: "#374151", border: "#d1d5db" },
                            { value: "green",  label: "✓ Clean Orders",  bg: "#f0fdf4", color: "#166534", border: "#22c55e" },
                            { value: "orange", label: "⚠ Errors but Ordered", bg: "#fff7ed", color: "#9a3412", border: "#f97316" },
                            { value: "red",    label: "✕ Failed / No Order",  bg: "#fef2f2", color: "#991b1b", border: "#ef4444" },
                        ].map(({ value, label, bg, color, border }) => (
                            <button
                                key={value}
                                onClick={() => { setHealthFilter(value); setPage(1); }}
                                style={{
                                    padding: "4px 10px",
                                    borderRadius: "4px",
                                    border: `2px solid ${healthFilter === value ? border : "#d1d5db"}`,
                                    background: healthFilter === value ? bg : "#fff",
                                    color: healthFilter === value ? color : "#6b7280",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    fontWeight: healthFilter === value ? "600" : "400",
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <span className="session-count" style={{marginLeft: "auto", fontSize: "13px", color: "#666", alignSelf: "center"}}>
                        {!loading && `${total} session${total !== 1 ? "s" : ""} found`}
                    </span>
                </div>

                <div className="filters">
                    <label style={{display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px", color: "#555"}}>
                        Status
                        <select name="status" value={filters.status} onChange={handleFilterChange}>
                            {STATUS_OPTIONS.map(s => (
                                <option key={s} value={s}>{s || "All"}</option>
                            ))}
                        </select>
                    </label>

                    <label style={{display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px", color: "#555"}}>
                        Search
                        <input
                            type="text"
                            placeholder="Search logs..."
                            name="search"
                            value={filters.search}
                            onChange={handleFilterChange}
                        />
                    </label>

                    <label style={{display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px", color: "#555"}}>
                        Store
                        <select name="store_code" value={filters.store_code || ""} onChange={handleFilterChange}>
                            <option value="">All Stores</option>
                            <option value="default">Shipping</option>
                            <option value="cateringen">Catering</option>
                            <option value="local_delivery_store_view">Localmenu</option>
                        </select>
                    </label>

                    <label style={{display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px", color: "#555"}}>
                        IP Address
                        <input
                            name="ip"
                            value={filters.ip}
                            onChange={handleFilterChange}
                            placeholder="e.g. 192.168"
                        />
                    </label>

                    <label style={{display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px", color: "#555"}}>
                        City
                        <input
                            name="city"
                            value={filters.city}
                            onChange={handleFilterChange}
                            placeholder="city"
                        />
                    </label>

                    <label style={{display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px", color: "#555"}}>
                        Start Date
                        <input
                            type="datetime-local"
                            name="startDate"
                            value={filters.startDate || ""}
                            onChange={handleFilterChange}
                        />
                    </label>

                    <label style={{display: "flex", flexDirection: "column", gap: "4px", fontSize: "12px", color: "#555"}}>
                        End Date
                        <input
                            type="datetime-local"
                            name="endDate"
                            value={filters.endDate || ""}
                            onChange={handleFilterChange}
                        />
                    </label>

                    <button onClick={applyFilters} className="btn btn-blue">Apply Filters</button>
                    <button onClick={clearFilters} className="btn">Clear</button>
                </div>

                {error   && <p style={{color: "red"}}>{error}</p>}
                {loading && <p style={{color: "#888"}}>Loading…</p>}

                {!loading && (
                    <div className="table-wrapper">
                        <table className="dashboard-table">
                            <thead>
                            <tr>
                                {COLUMNS.map(({label, field}) => (
                                    <th
                                        key={label}
                                        onClick={() => handleSort(field)}
                                        style={{
                                            borderBottom: "1px solid #ddd",
                                            padding: "10px",
                                            textAlign: "left",
                                            background: "#f9f9f9",
                                            cursor: field ? "pointer" : "default",
                                            userSelect: "none",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {label}
                                        {field && <SortIcon active={sortBy === field} order={sortOrder}/>}
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {sessions.length === 0 ? (
                                <tr>
                                    <td colSpan={9} style={{...CELL, textAlign: "center", color: "#888", padding: "30px"}}>
                                        No sessions match your filters.
                                    </td>
                                </tr>
                            ) : sessions.map(s => (
                                <tr key={s._id}
                                    style={{
                                        borderLeft: s.session_health === "green"  ? "4px solid #22c55e" :
                                            s.session_health === "orange" ? "4px solid #f97316" :
                                                s.session_health === "red"    ? "4px solid #ef4444" :
                                                    "4px solid transparent",
                                        background: s.session_health === "green"  ? "#f0fdf4" :
                                            s.session_health === "orange" ? "#fff7ed" :
                                                s.session_health === "red"    ? "#fef2f2" :
                                                    "",
                                        transition: "opacity 0.15s",
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
                                    onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                    className={s.is_bot ? "bot-row" : ""}
                                >
                                    <td>
                                        <a
                                            className="session-link"
                                            href={`/session/${s._id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {s._id}
                                        </a>
                                    </td>
                                    <td>{s.store_name} ({s.store_code})</td>
                                    <td>{new Date(s.first_event).toLocaleString("en-US", {timeZoneName: "short"})}</td>
                                    <td>{new Date(s.last_event).toLocaleString("en-US", {timeZoneName: "short"})}</td>
                                    <td>{s.ip_address}</td>
                                    <td>{s.city}</td>
                                    <td>{s.region}</td>
                                    <td className="event-count">{s.total_events}</td>
                                    <td>
                                        {s.is_bot ? (
                                            <span className="status-badge bot">Bot</span>
                                        ) : (
                                            <span className={`status-badge ${s.status ? "status-active" : "status-null"}`}>
                                                {s.status || "—"}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && totalPages > 0 && (
                    <div style={{display: "flex", alignItems: "center", gap: "10px", marginTop: "16px", flexWrap: "wrap"}}>
                        <button onClick={() => setPage(1)}           disabled={page === 1}          style={BTN(page === 1)}>«</button>
                        <button onClick={() => setPage(p => p - 1)} disabled={page === 1}          style={BTN(page === 1)}>‹ Prev</button>

                        <span style={{fontSize: "13px", color: "#555"}}>
                            Page {page} of {totalPages}
                        </span>

                        <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} style={BTN(page === totalPages)}>Next ›</button>
                        <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={BTN(page === totalPages)}>»</button>

                        <label style={{marginLeft: "auto", fontSize: "13px", color: "#555", display: "flex", alignItems: "center", gap: "6px"}}>
                            Rows per page
                            <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} style={INPUT}>
                                {LIMIT_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                    </div>
                )}

            </div>
        </div>
    );
}

export default Sessions;